import React from 'react';
import { PieChart, Utensils, Apple, Beef, Milk } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения распределения макронутриентов (белки, жиры, углеводы)
 */
const NutritionalBreakdown = ({ results, formData, dietTypes }) => {
  if (!results) return null;

  // Получаем информацию о выбранном типе диеты
  const selectedDiet = dietTypes.find(diet => diet.id === formData.dietType) || dietTypes[0];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Utensils className="w-5 h-5 mr-2 text-emerald-500" />
          Распределение макронутриентов
        </h3>
        <span className="text-sm bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 py-1 px-3 rounded-full">
          {selectedDiet.name}
        </span>
      </div>

      {/* Общий обзор макронутриентов */}
      <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Суточная норма калорий */}
          <div className="text-center sm:text-left flex-1">
            <h4 className="text-sm text-emerald-700 dark:text-emerald-300 mb-1">Суточная норма</h4>
            <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
              {formatNumber(results.targetCalories, 0)} ккал
            </div>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
              {formData.goal === 'lose' 
                ? 'Для снижения веса' 
                : formData.goal === 'gain' 
                  ? 'Для набора массы' 
                  : 'Для поддержания веса'}
            </p>
          </div>

          {/* Визуализация соотношения БЖУ */}
          <div className="w-36 h-36 relative">
            <PieChartComponent 
              data={[
                { value: results.macros.proteinPercent, color: '#34d399' }, // Protein - green
                { value: results.macros.fatPercent, color: '#fbbf24' },     // Fat - yellow
                { value: results.macros.carbsPercent, color: '#60a5fa' }    // Carbs - blue
              ]}
            />
            <div className="absolute inset-0 flex items-center justify-center flex-col">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">БЖУ</span>
              <span className="text-xs text-gray-500 dark:text-gray-500">
                {results.macros.proteinPercent}/{results.macros.fatPercent}/{results.macros.carbsPercent}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Детальная разбивка по макронутриентам */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Белки */}
        <MacronutrientCard 
          name="Белки" 
          grams={results.macros.protein} 
          calories={results.macros.protein * 4} 
          percentage={results.macros.proteinPercent}
          icon={<Beef className="w-5 h-5 text-emerald-500" />}
          color="emerald"
          description="Строительный материал для мышц, важны для восстановления и роста тканей"
        />
        
        {/* Жиры */}
        <MacronutrientCard 
          name="Жиры" 
          grams={results.macros.fat} 
          calories={results.macros.fat * 9} 
          percentage={results.macros.fatPercent}
          icon={<Milk className="w-5 h-5 text-amber-500" />}
          color="amber"
          description="Необходимы для гормонального баланса и усвоения витаминов"
        />
        
        {/* Углеводы */}
        <MacronutrientCard 
          name="Углеводы" 
          grams={results.macros.carbs} 
          calories={results.macros.carbs * 4} 
          percentage={results.macros.carbsPercent}
          icon={<Apple className="w-5 h-5 text-blue-500" />}
          color="blue"
          description="Основной источник энергии для организма и мозга"
        />
      </div>

      {/* Рекомендации по питанию */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <PieChart className="w-4 h-4 mr-2 text-emerald-500" />
          Рекомендации по питанию
        </h4>
        <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start">
            <div className="w-5 h-5 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center text-emerald-700 dark:text-emerald-400 mr-2 mt-0.5 flex-shrink-0">1</div>
            <p>Старайтесь распределять калории и макронутриенты равномерно в течение дня, избегая слишком больших перерывов между приемами пищи.</p>
          </li>
          <li className="flex items-start">
            <div className="w-5 h-5 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center text-emerald-700 dark:text-emerald-400 mr-2 mt-0.5 flex-shrink-0">2</div>
            <p>Выбирайте качественные источники белка: нежирное мясо, рыбу, яйца, молочные продукты, бобовые.</p>
          </li>
          <li className="flex items-start">
            <div className="w-5 h-5 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center text-emerald-700 dark:text-emerald-400 mr-2 mt-0.5 flex-shrink-0">3</div>
            <p>Отдавайте предпочтение полезным жирам: авокадо, орехи, оливковое масло, жирная рыба.</p>
          </li>
          <li className="flex items-start">
            <div className="w-5 h-5 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center text-emerald-700 dark:text-emerald-400 mr-2 mt-0.5 flex-shrink-0">4</div>
            <p>Выбирайте сложные углеводы вместо простых: цельнозерновые продукты, овощи, фрукты.</p>
          </li>
          <li className="flex items-start">
            <div className="w-5 h-5 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center text-emerald-700 dark:text-emerald-400 mr-2 mt-0.5 flex-shrink-0">5</div>
            <p>Не забывайте о потреблении клетчатки (25-35 г в день) – она обеспечивает чувство сытости и нормализует пищеварение.</p>
          </li>
        </ul>
      </div>
    </div>
  );
};

/**
 * Компонент карточки макронутриента
 */
const MacronutrientCard = ({ name, grams, calories, percentage, icon, color, description }) => {
  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-200 dark:border-emerald-800',
      text: 'text-emerald-700 dark:text-emerald-300',
      progress: 'bg-emerald-500'
    },
    amber: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
      text: 'text-amber-700 dark:text-amber-300',
      progress: 'bg-amber-500'
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-700 dark:text-blue-300',
      progress: 'bg-blue-500'
    }
  };

  const classes = colorClasses[color] || colorClasses.emerald;

  return (
    <div className={`${classes.bg} rounded-lg border ${classes.border} p-4`}>
      <div className="flex items-center mb-3">
        {icon}
        <h4 className={`ml-2 font-medium ${classes.text}`}>{name}</h4>
      </div>
      
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">{grams} г</span>
        <span className="text-sm text-gray-500 dark:text-gray-400">{calories} ккал</span>
      </div>
      
      <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mb-3">
        <div className={`h-1.5 rounded-full ${classes.progress}`} style={{ width: `${percentage}%` }}></div>
      </div>
      
      <p className="text-xs text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  );
};

/**
 * Компонент круговой диаграммы
 */
const PieChartComponent = ({ data }) => {
  // Рассчитываем общую сумму
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  // Рассчитываем углы для каждого сегмента
  let startAngle = 0;
  const segments = data.map(item => {
    const angle = (item.value / total) * 360;
    const segment = {
      startAngle,
      endAngle: startAngle + angle,
      color: item.color
    };
    startAngle += angle;
    return segment;
  });

  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <circle cx="50" cy="50" r="45" fill="#f3f4f6" className="dark:fill-gray-700" />
      {segments.map((segment, index) => {
        const x1 = 50 + 45 * Math.cos((segment.startAngle - 90) * Math.PI / 180);
        const y1 = 50 + 45 * Math.sin((segment.startAngle - 90) * Math.PI / 180);
        const x2 = 50 + 45 * Math.cos((segment.endAngle - 90) * Math.PI / 180);
        const y2 = 50 + 45 * Math.sin((segment.endAngle - 90) * Math.PI / 180);
        
        // Определяем, больше ли сегмент половины круга
        const largeArcFlag = segment.endAngle - segment.startAngle > 180 ? 1 : 0;
        
        // Создаем путь для сегмента
        const path = `M 50 50 L ${x1} ${y1} A 45 45 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
        
        return <path key={index} d={path} fill={segment.color} />
      })}
      <circle cx="50" cy="50" r="25" fill="white" className="dark:fill-gray-800" />
    </svg>
  );
};

export default NutritionalBreakdown;